import { supabase } from "@/integrations/supabase/client";

const P = 1;

/**
 * Snapshot the current MM portfolio fund-duration state.
 * Captures one row per held fund + the weighted-average portfolio duration at snapshot time.
 */
export async function snapshotMmFundDurations(reason: "manual_edit" | "trade", tradeId: string | null) {
  const [weightsRes, durationsRes] = await Promise.all([
    supabase.from("ba_mm_cbam_fund_weights").select("fund_id, weight_pct, effective_date").eq("portfolio", P),
    supabase.from("ba_mm_fund_durations" as any).select("fund_id, duration_years"),
  ]);
  if (weightsRes.error) throw weightsRes.error;
  if (durationsRes.error) throw durationsRes.error;

  const durMap = new Map<string, number | null>();
  for (const d of ((durationsRes.data || []) as unknown) as Array<{ fund_id: string; duration_years: number | null }>) {
    durMap.set(d.fund_id, d.duration_years);
  }

  // The table is effective-dated and an exit is an explicit weight_pct = 0 row at the exit
  // date, so reduce to the latest row per fund before filtering — older rows describe past
  // holdings, not the current portfolio.
  const latestByFund = new Map<string, any>();
  for (const w of (weightsRes.data || []) as any[]) {
    const prev = latestByFund.get(w.fund_id);
    if (!prev || w.effective_date > prev.effective_date) latestByFund.set(w.fund_id, w);
  }
  const weights = Array.from(latestByFund.values()).filter((w: any) => Number(w.weight_pct) > 0);

  let num = 0, den = 0;
  for (const w of weights as Array<{ fund_id: string; weight_pct: number }>) {
    const d = durMap.get(w.fund_id);
    if (d != null) { num += Number(w.weight_pct) * Number(d); den += Number(w.weight_pct); }
  }
  const wAvg = den > 0 ? num / den : null;

  const { data: { user } } = await supabase.auth.getUser();
  const rows = (weights as Array<{ fund_id: string; weight_pct: number }>).map((w) => ({
    reason,
    trade_id: tradeId,
    fund_id: w.fund_id,
    duration_years: durMap.get(w.fund_id) ?? null,
    weight_pct: Number(w.weight_pct),
    weighted_avg_duration: wAvg,
    created_by: user?.id ?? null,
  }));

  if (rows.length === 0) return;
  const { error } = await supabase.from("ba_mm_fund_duration_snapshots" as any).insert(rows);
  if (error) throw error;
}
